import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QuoteOutlineIcon } from '@repo/atomic-components';
import { BasePdfSinglePageRenderer } from '@repo/atomic-components/pdf';
import {
  usePdfPageHighlightText,
  usePdfPageAnnotation,
  useAnnotationLabelsContext,
  usePdfPageTextSelection,
} from '../hooks';
import { BundleItemDataExtended } from '@/api';
import { selectPageInfoByPageId, useAppSelector } from '@/store';

type PdfPageContainerProps = {
  item: BundleItemDataExtended;
  pageHeight: number;
  pageWidth: number;
  onOpenChronologyDialog: (text: string, pageId: string) => void;
};

export const PdfPageContainer: FC<PdfPageContainerProps> = ({
  item,
  pageHeight,
  pageWidth,
  onOpenChronologyDialog,
}) => {
  const { showFilteredLabels, filterAnnotationLabels } = useAnnotationLabelsContext();
  const pageInputRef = useRef<HTMLDivElement | null>(null);

  // Smooth zoom via CSS scale + snapshot overlay. When zoom changes:
  //   1. pageWidth/pageHeight update immediately (row layout is correct)
  //   2. We hold committedSize steady and CSS-scale the existing canvas
  //      to visually fill the new size — no white flash, no re-raster yet
  //   3. 200ms after the last zoom click, we drawImage-copy the current
  //      canvas into an overlay canvas, show that overlay at the target
  //      size, then commit the new size so pdf.js re-renders the canvas
  //      underneath. Copying pixels via drawImage avoids the cost of
  //      PNG-encoding at high zoom / high DPR that toDataURL would incur.
  //   4. When onPageRenderSuccess fires, we drop the overlay — user only
  //      ever sees stretched-old → sharp-new, never a grey gap
  const [committedSize, setCommittedSize] = useState<{ width: number; height: number }>({
    width: pageWidth,
    height: pageHeight,
  });
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const hasSnapshotRef = useRef(false);
  const overlayFailsafeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Max time the snapshot overlay is allowed to sit on top of a page while
  // waiting for pdf.js. If onPageRenderSuccess doesn't fire (render error,
  // aborted mid-flight, etc.) we hide the overlay so users aren't stuck
  // looking at a stale image over broken content.
  const OVERLAY_FAILSAFE_MS = 2000;

  const clearOverlayFailsafe = useCallback(() => {
    if (overlayFailsafeRef.current !== null) {
      clearTimeout(overlayFailsafeRef.current);
      overlayFailsafeRef.current = null;
    }
  }, []);

  const setSnapshotVisible = useCallback(
    (visible: boolean) => {
      hasSnapshotRef.current = visible;
      setHasSnapshot(visible);
      if (!visible) clearOverlayFailsafe();
    },
    [clearOverlayFailsafe],
  );

  const captureSnapshot = useCallback((): boolean => {
    const root = pageInputRef.current;
    if (!root) return false;
    const src = root.querySelector('canvas');
    const dst = overlayCanvasRef.current;
    if (!src || !dst || src.width === 0 || src.height === 0) return false;
    // Assigning width/height resets the canvas, so the previous snapshot
    // is cleared before we draw the new one.
    dst.width = src.width;
    dst.height = src.height;
    const ctx = dst.getContext('2d');
    if (!ctx) return false;
    try {
      ctx.drawImage(src, 0, 0);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (committedSize.width === pageWidth && committedSize.height === pageHeight) return;
    const id = setTimeout(() => {
      // If a snapshot is already showing, the source canvas is currently
      // mid-render for the previous commit — snapshotting now can capture a
      // blank or half-drawn frame. Keep the existing overlay (which is
      // CSS-stretched to the new target size anyway) and just commit.
      if (!hasSnapshotRef.current && captureSnapshot()) {
        setSnapshotVisible(true);
      }
      // Either way, if an overlay is up we're about to commit a new size
      // that will restart the pdf.js render. Reset the failsafe so its 2s
      // countdown measures from THIS commit, not the first one — otherwise
      // rapid successive zooms let the original timer expire mid-render
      // and drop the overlay before the fresh canvas is ready.
      if (hasSnapshotRef.current) {
        clearOverlayFailsafe();
        overlayFailsafeRef.current = setTimeout(() => setSnapshotVisible(false), OVERLAY_FAILSAFE_MS);
      }
      setCommittedSize({ width: pageWidth, height: pageHeight });
    }, 200);
    return () => clearTimeout(id);
  }, [
    pageWidth,
    pageHeight,
    committedSize.width,
    committedSize.height,
    captureSnapshot,
    setSnapshotVisible,
    clearOverlayFailsafe,
  ]);

  useEffect(() => clearOverlayFailsafe, [clearOverlayFailsafe]);

  const handlePageRenderSuccess = useCallback(() => {
    setSnapshotVisible(false);
  }, [setSnapshotVisible]);

  const scale = committedSize.width > 0 ? pageWidth / committedSize.width : 1;
  const needsScale = scale !== 1;

  // Subscribe to only this page's slice of the info cache. Subscribing to the
  // whole cache caused every visible PDF to re-render (and re-run annotation /
  // text-layer hooks) on every prefetch batch, because Immer produces a new
  // cache reference whenever any page is merged in.
  const cachedInfo = useAppSelector(selectPageInfoByPageId(item.pageId));

  // Annotation and text-layer hooks compute pixel coordinates against the
  // canvas react-pdf actually rendered — that's the committed size, not the
  // in-flight zoom target. Passing pageWidth/pageHeight here would offset
  // the highlights from where they should sit during the scale window.
  const { onGetAnnotationsSuccess } = usePdfPageAnnotation({
    pageId: item.pageId,
    datesLabels: cachedInfo?.dates ?? item.dates,
    textLabels: cachedInfo?.textLabels ?? item.textLabels,
    height: committedSize.height,
    width: committedSize.width,
    showFilteredLabels: showFilteredLabels,
    filterAnnotationLabels: filterAnnotationLabels,
    pageInputRef,
  });

  const { onRenderTextLayerSuccess } = usePdfPageHighlightText({
    pageInputRef: pageInputRef,
    pageId: item.pageId,
    height: committedSize.height,
    width: committedSize.width,
  });

  // No `onContextMenu` handler here — we deliberately let the browser's
  // native right-click menu appear on the PDF. The floating "Add to
  // Chronology" button (rendered below when there's a selection) is the
  // app-specific replacement for the old custom menu items.
  const { lastSelection, clearSelection } = usePdfPageTextSelection({
    pageId: item.pageId,
    pageInputRef: pageInputRef,
  });

  // Position of the "Add to chronology" floating button. Viewport-space
  // coordinates (position: fixed) so the button can escape the PDF page's
  // overflow: hidden wrapper — anchoring inside the page div clipped the
  // button whenever the selection was near a page edge.
  const [floatingBtnAnchor, setFloatingBtnAnchor] = useState<{ top: number; left: number } | null>(null);

  const recomputeFloatingBtnAnchor = useCallback(() => {
    if (!pageInputRef.current) {
      setFloatingBtnAnchor(null);
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.toString().trim()) {
      setFloatingBtnAnchor(null);
      return;
    }
    const range = selection.getRangeAt(0);
    if (!pageInputRef.current.contains(range.commonAncestorContainer)) {
      setFloatingBtnAnchor(null);
      return;
    }
    const rects = range.getClientRects();
    const endRect = rects[rects.length - 1];
    if (!endRect) {
      setFloatingBtnAnchor(null);
      return;
    }
    setFloatingBtnAnchor({
      top: endRect.bottom + 4,
      // Centre the button horizontally under the end of the selection —
      // the button applies `translateX(-50%)` so its middle sits at this x.
      left: endRect.left + endRect.width / 2,
    });
  }, []);

  useEffect(() => {
    // Keep the anchor in sync with whatever the selection hook is tracking.
    // `lastSelection` becomes truthy just after `selectionchange` fires, so
    // reading rects here always sees a settled selection.
    if (lastSelection) {
      recomputeFloatingBtnAnchor();
    } else {
      setFloatingBtnAnchor(null);
    }
  }, [lastSelection, recomputeFloatingBtnAnchor]);

  // Re-anchor on scroll so the button tracks the selection as the user
  // scrolls the virtualised list. Capture phase catches scrolls on
  // ancestor containers (the list wrapper) too, not just the window.
  useEffect(() => {
    if (!lastSelection) return;
    const onScroll = () => recomputeFloatingBtnAnchor();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [lastSelection, recomputeFloatingBtnAnchor]);

  const handleAddToChronologyClick = () => {
    const cleanedText = (lastSelection || '').replace(/[\r\n]+/g, ' ').trim();
    onOpenChronologyDialog(cleanedText, item.pageId);
    clearSelection();
  };

  // Floating button appears whenever the user has an active text selection —
  // whether or not the chronology dialog is already open. Its click handler
  // routes through the same `handleAddToChronology` that deduplicates pageIds
  // and appends text, so an already-open dialog just accumulates instead of
  // re-opening (matches the "Add to Entry" right-click flow).
  const canShowFloatingBtn = !!lastSelection && !!floatingBtnAnchor;

  return (
    <>
      {/* Outer wrapper is at the TARGET zoom size so row layout is correct.
          overflow: hidden clips scaled content that would otherwise leak
          into adjacent rows during the debounce. */}
      <div
        style={{
          position: 'relative',
          width: `${pageWidth}px`,
          height: `${pageHeight}px`,
          overflow: 'hidden',
        }}
      >
        {/* Inner wrapper is at committed size and CSS-scaled to fill the
            outer. During the debounce, this makes the existing canvas
            visually stretch to the target size — instant, no flash. */}
        <div
          style={{
            width: `${committedSize.width}px`,
            height: `${committedSize.height}px`,
            transform: needsScale ? `scale(${scale})` : undefined,
            transformOrigin: 'top left',
          }}
        >
          <BasePdfSinglePageRenderer
            documentClassName="flex items-center justify-center"
            pageClassName="flex items-center"
            pdfFile={item.pageUri}
            height={committedSize.height}
            width={committedSize.width}
            mainSide="width"
            rotate={item.pageDimensions.pageRotation}
            pageInputRef={pageInputRef}
            onGetAnnotationsSuccess={onGetAnnotationsSuccess}
            onRenderTextLayerSuccess={onRenderTextLayerSuccess}
            onPageRenderSuccess={handlePageRenderSuccess}
          />
        </div>
        {/* Snapshot overlay: bridges the gap while pdf.js re-renders the
            canvas at the newly-committed size. A sibling canvas holds a
            drawImage copy of the old pixels — cheaper than toDataURL, which
            has to PNG-encode + base64. CSS width/height stretch it to the
            target zoom size; pointer-events-none so text selection on the
            fresh canvas underneath still works. */}
        <canvas
          ref={overlayCanvasRef}
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${pageWidth}px`,
            height: `${pageHeight}px`,
            pointerEvents: 'none',
            display: hasSnapshot ? 'block' : 'none',
          }}
        />
      </div>
      {canShowFloatingBtn &&
        createPortal(
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAddToChronologyClick}
            style={{
              position: 'fixed',
              top: floatingBtnAnchor.top,
              left: floatingBtnAnchor.left,
              transform: 'translateX(-50%)',
              zIndex: 50,
            }}
            className="border-fletchers-gray-110 bg-fletchers-white hover:bg-fletchers-gray-50 flex h-[32px] cursor-pointer items-center gap-2 rounded-[4px] border px-4 text-[14px] font-semibold whitespace-nowrap text-black shadow-[0px_4px_12px_0px_#00000040]"
          >
            <QuoteOutlineIcon width={16} className="text-black" />
            Add to Chronology
          </button>,
          document.body,
        )}
    </>
  );
};
